import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format, startOfWeek, addWeeks } from "date-fns";
import { CloudOff } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { haptic } from "@/lib/haptics";
import {
  apiGetTimesheetWeek, apiEnsureTimesheetWeek, apiSaveTimesheetEntries,
  apiSubmitTimesheet, apiCopyPreviousWeek, apiGetTimekeepingTasks,
  apiGetTimekeepingSettings,
  ApiError, type TimesheetWeek, type TimesheetEntryInput,
} from "@/lib/api";
import { WeekFlow } from "@/components/timekeeping/WeekFlow";
import { queueSave, getQueued, removeQueued, hasPending, flushQueued } from "@/lib/timekeeping-offline";
import MobileLayout from "./MobileLayout";
import { getTimekeepingErrorMessage } from "@/lib/timekeeping-errors";

const toISO = (d: Date) => format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");

/** Overlay queued-offline entries onto the server week so the flow shows them. */
function mergeQueued(week: TimesheetWeek, entries: TimesheetEntryInput[]): TimesheetWeek {
  const byDate: Record<string, TimesheetWeek["days"][number]["entries"]> = {};
  for (const e of entries) {
    const date = e.entryDate.slice(0, 10);
    (byDate[date] ||= []).push({
      id: 0, timesheet_id: 0, entry_date: e.entryDate, task_id: e.taskId ?? null,
      hours: e.hours, start_time: null, end_time: null, break_minutes: e.breakMinutes ?? 0, notes: e.notes ?? null,
    });
  }
  return { ...week, days: week.days.map((d) => ({ ...d, entries: byDate[d.date] ?? [] })) };
}

export default function MobileTimekeeping() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [anchor, setAnchor] = useState<Date>(new Date());
  const weekISO = toISO(anchor);
  const [online, setOnline] = useState<boolean>(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pendingSync, setPendingSync] = useState<boolean>(hasPending());
  const [syncTick, setSyncTick] = useState(0);

  const { data: week, isLoading } = useQuery<TimesheetWeek>({
    queryKey: ["mobile-timesheet-week", weekISO],
    queryFn: () => apiGetTimesheetWeek(weekISO),
  });
  const { data: tasks = [] } = useQuery({
    queryKey: ["timekeeping-tasks"],
    queryFn: () => apiGetTimekeepingTasks(false),
  });
  const { data: settings } = useQuery({
    queryKey: ["timekeeping-settings"],
    queryFn: apiGetTimekeepingSettings,
  });

  // Track connectivity + replay any queued saves when it returns.
  useEffect(() => {
    const flush = async () => {
      setOnline(navigator.onLine);
      if (!navigator.onLine) return;
      const n = await flushQueued(async (wISO, tid, entries) => {
        const id = tid ?? (await apiEnsureTimesheetWeek(wISO)).timesheetId;
        if (id == null) throw new Error(t("timekeeping.errors.createWeekFailed", "The week could not be created."));
        return apiSaveTimesheetEntries(id, entries);
      });
      setPendingSync(hasPending());
      if (n > 0) { setSyncTick((v) => v + 1); qc.invalidateQueries({ queryKey: ["mobile-timesheet-week"] }); }
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", flush);
    window.addEventListener("offline", goOffline);
    flush();
    return () => {
      window.removeEventListener("online", flush);
      window.removeEventListener("offline", goOffline);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // `getQueued` reads the localStorage offline queue — an external store React
  // cannot track — so `syncTick` and `pendingSync` are deliberate recompute
  // triggers, bumped wherever that queue changes (save, flush, copy-week).
  // ESLint sees them as unused because they are not referenced in the body;
  // dropping them would leave a stale offline merge on screen after a sync.
  const displayWeek = useMemo(() => {
    if (!week) return week;
    const q = getQueued(weekISO);
    return q ? mergeQueued(week, q.entries) : week;
  }, [week, weekISO, syncTick, pendingSync]); // eslint-disable-line react-hooks/exhaustive-deps

  const ensureId = async (): Promise<number> => {
    if (week?.timesheetId != null) return week.timesheetId;
    const ensured = await apiEnsureTimesheetWeek(weekISO);
    if (ensured.timesheetId == null) throw new Error(t("timekeeping.errors.createWeekFailed", "The week could not be created."));
    return ensured.timesheetId;
  };

  const setWeekData = (u: TimesheetWeek) => qc.setQueryData(["mobile-timesheet-week", weekISO], u);

  const saveMut = useMutation({
    mutationFn: async (entries: TimesheetEntryInput[]): Promise<TimesheetWeek | null> => {
      if (!navigator.onLine) { queueSave(weekISO, week?.timesheetId ?? null, entries); return null; }
      try {
        const u = await apiSaveTimesheetEntries(await ensureId(), entries);
        return u;
      } catch (error) {
        if (error instanceof ApiError) throw error;
        queueSave(weekISO, week?.timesheetId ?? null, entries);
        return null;
      }
    },
    onSuccess: (u) => {
      if (u) { setWeekData(u); removeQueued(weekISO); }
      else { toast.message(t("timekeeping.mobile.savedOffline", { defaultValue: "Saved offline — will sync when back online" })); }
      setPendingSync(hasPending());
      setSyncTick((v) => v + 1);
    },
    onError: (e) => toast.error(getTimekeepingErrorMessage(e, t)),
  });

  const submitMut = useMutation({
    mutationFn: async (entries: TimesheetEntryInput[]) => {
      if (!navigator.onLine) throw new Error(t("timekeeping.mobile.needOnline", { defaultValue: "Connect to the internet to submit." }));
      const id = await ensureId();
      await apiSaveTimesheetEntries(id, entries);
      removeQueued(weekISO);
      return apiSubmitTimesheet(id);
    },
    onSuccess: (u) => { setWeekData(u); setPendingSync(hasPending()); haptic(); toast.success(t("timekeeping.submit.done", { defaultValue: "Week submitted for approval" })); },
    onError: (e) => toast.error(getTimekeepingErrorMessage(e, t)),
  });

  const copyMut = useMutation({
    mutationFn: async () => apiCopyPreviousWeek(await ensureId(), { mode: "all", includeNotes: true }),
    onSuccess: (u) => { setWeekData(u); removeQueued(weekISO); setSyncTick((v) => v + 1); haptic(); toast.success(t("timekeeping.copy.done", { defaultValue: "Previous week copied" })); },
    onError: (e) => toast.error(getTimekeepingErrorMessage(e, t)),
  });

  const editable = !!displayWeek && (displayWeek.status === "Draft" || displayWeek.status === "Needs Changes");

  return (
    <MobileLayout title={t("timekeeping.title", { defaultValue: "Timekeeping" })} showBack>
      <div className="px-4 py-4 space-y-4 overflow-x-hidden">
        {(!online || pendingSync) && (
          <div className="flex items-center gap-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 px-3 py-2 text-xs">
            <CloudOff className="h-4 w-4 shrink-0" />
            {!online
              ? t("timekeeping.mobile.offline", { defaultValue: "You're offline. Changes are saved on this device and will sync automatically." })
              : t("timekeeping.mobile.pendingSync", { defaultValue: "Some changes are waiting to sync." })}
          </div>
        )}

        {isLoading || !displayWeek ? (
          <Skeleton className="h-96 w-full rounded-2xl" />
        ) : (
          <WeekFlow
            week={displayWeek}
            tasks={tasks}
            editable={editable}
            online={online}
            saving={saveMut.isPending}
            submitting={submitMut.isPending}
            maxDailyHours={settings?.maxDailyHours}
            allowFutureDates={settings?.allowFutureDates}
            onPrevWeek={() => { haptic(); setAnchor(addWeeks(anchor, -1)); }}
            onNextWeek={() => { haptic(); setAnchor(addWeeks(anchor, 1)); }}
            onThisWeek={() => setAnchor(new Date())}
            onSaveDraft={(entries) => saveMut.mutate(entries)}
            onSubmit={(entries) => submitMut.mutate(entries)}
            onCopyPrevious={() => copyMut.mutate()}
          />
        )}
      </div>
    </MobileLayout>
  );
}
